import { useEffect, useState } from "react";
import axios from "axios";
import { Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const t = (ts) => (ts ? new Date(ts * 1000).toLocaleTimeString() : "never");

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-[#1A2338] last:border-0 text-xs" data-testid={testid}>
    <span className="text-slate-400">{k}</span><span className="font-num text-right break-all">{v}</span>
  </div>
);

export const DiagnosticsCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/diagnostics`).then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  if (!d) return null;
  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="diagnostics-card">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-[#22D3EE]" /> Diagnostics</h2>
      <div className="grid lg:grid-cols-2 gap-4 mt-3">
        <div>
          <Row k="Data status" v={d.data_status || "—"} testid="diag-data-status" />
          <Row k="Current market" v={d.market || "—"} testid="diag-market" />
          <Row k="Last price update" v={`${t(d.last_price_update)} (age ${d.price_age_sec ?? "—"}s)`} testid="diag-last-price" />
          <Row k="Last calculation" v={`${t(d.last_calculation)} (age ${d.calc_age_sec ?? "—"}s)`} testid="diag-last-calc" />
          <Row k="Last Telegram delivery" v={t(d.telegram?.last_delivery)} testid="diag-last-telegram" />
          <Row k="Telegram failures / retries" v={`${d.telegram?.failures} / ${d.telegram?.retries}`} />
          <Row k="Model" v={`${d.model?.version} · ${d.model?.validation}`} testid="diag-model" />
          <Row k="Freshness limits" v={d.freshness?.limits ? `BRTI ${d.freshness.limits.index_sec}s · book ${d.freshness.limits.book_sec}s` : "—"} />
          <Row k="Overlapping ticks skipped / lease denied" v={`${d.loop?.ticks_skipped_overlap} / ${d.loop?.lease_denied}`} />
          {d.current_error && <Row k="Current error" v={d.current_error} testid="diag-current-error" />}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400 mb-1">Data sources</div>
          {Object.entries(d.sources || {}).map(([k, s]) => (
            <Row key={k} k={k} testid={`diag-source-${k}`}
              v={`ok ${s.ok} · err ${s.errors} · retry ${s.retries} · ${s.latency_ms ?? "—"}ms${s.last_error ? ` · last ${s.last_error.error}` : ""}`} />
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-4 text-[11px]">
        {[["Recent state transitions", d.recent_transitions, (r) => `${t(r.at)} ${r.from || "—"} → ${r.status}`, "diag-transitions"],
          ["Recent notification decisions", d.recent_notifications, (r) => `${new Date(r.created_at).toLocaleTimeString()} ${r.type} · ${r.sent ? "sent" : r.suppressed || "not sent"}`, "diag-notifications"],
          ["Recent calculation errors", d.recent_calc_errors, (r) => `${t(r.at)} ${r.error}`, "diag-errors"]].map(([title, rows, fmt, id]) => (
          <div key={id} data-testid={id}>
            <div className="text-[10px] uppercase tracking-widest font-num text-slate-400 mb-1">{title}</div>
            {(rows || []).length ? rows.slice(0, 8).map((r, i) => <div key={i} className="font-num text-slate-300 py-0.5 break-words">{fmt(r)}</div>)
              : <div className="text-slate-500">None</div>}
          </div>
        ))}
      </div>
    </section>
  );
};
